/**
 * EuHistoricalCompare — section-synchronized two-pane compare for a single EU
 * drug's archived historical SmPC versions. Embedded in the EU drug viewer
 * (launched from the version dropdown), NOT a standalone browse page.
 *
 * Model mirrors the US "Version Comparison": a plain count summary
 * (N total · X added · Y modified · Z removed · W renumbered · V unchanged) +
 * simple verdict filters. Left rail = the section list; centre/right = the two
 * authentic PDFs with red (removed) / green (added) highlights. No review /
 * flag / note workflow, no subjective priority — just what changed, clearly.
 *
 * Diffs come pre-stored from eu_version_diffs (backend read-through cache); the
 * PDFs live in SOR. Data: /api/eu-compare-proto.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { auth } from '../../config/firebase';
import { useTab } from '../../shell/TabContext';

const BASE_URL =
  (import.meta.env['VITE_API_URL'] as string) ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

async function authHeaders(): Promise<Record<string, string>> {
  const u = auth.currentUser;
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (u) h['Authorization'] = `Bearer ${await u.getIdToken(false)}`;
  return h;
}
async function apiGet<T>(path: string): Promise<T> {
  const r = await fetch(`${BASE_URL}${path}`, { headers: await authHeaders() });
  if (!r.ok) throw new Error(`${r.status}: ${await r.text()}`);
  return r.json() as Promise<T>;
}

interface Ver { vstr: string; date: string; docid: string }
interface PageInfo { page: number; w: number; h: number; img: string }
type Verdict = 'unchanged' | 'modified' | 'added' | 'removed' | 'renumbered';
// One changed figure's location (op = 'added' → new pane, 'removed' → old pane).
interface FigMark { op: string; page: number; y: number; box: [number, number, number, number] }
export interface Section {
  num: string; title: string; old_num: string; new_num: string;
  old_page: number | null; new_page: number | null;
  old_y: number | null; new_y: number | null;
  verdict: Verdict;
  words_added: number; words_removed: number;
  words_moved?: number;   // same text, new position within the section
  figures_added?: number; figures_removed?: number;
  // First changed figure's page + normalized top-y per side (flag-ON payload) — the
  // viewer lands here on a figure-row click; null when that side has no figure change.
  fig_old_page?: number | null; fig_old_y?: number | null;
  fig_new_page?: number | null; fig_new_y?: number | null;
  // Every changed figure in this section (reading order) for step-through nav.
  fig_marks?: FigMark[];
  summary_add: string; summary_del: string;
  // PART-AWARE identity (flag-ON Parser-B payload). Absent on the legacy
  // SmPC-only payload; when present, two parts' same §N (SmPC §4 vs leaflet §4)
  // are DISTINCT rows and must not collapse. See ``sectionKey``.
  document_part?: string | null;
  presentation_key?: string | null;
  presentation_label?: string | null;
  // structure-tree link fields — join a tree leaf to its diff row by anchor_id.
  anchor_id?: string | null;
  parent_anchor_id?: string | null;
  node_kind?: string | null;
  level?: number | null;
}

// A serialized Parser-B structure node (nav authority). The rail renders the
// hierarchy from these — part → presentation group → section → subsection — and
// joins each section leaf to its diff Section row by ``anchor_id``.
interface StructNode {
  anchor_id: string;
  parent_anchor_id: string | null;
  document_part: string;
  presentation_key: string | null;
  presentation_label: string | null;
  section_number: string | null;
  section_title: string | null;
  node_kind: string | null;
  is_group: boolean;
  level: number | null;
  start_page: number | null;
  start_y: number | null;
}

// Human labels for the four EU document parts (rail grouping headers).
const PART_LABELS: Record<string, string> = {
  smpc: 'SmPC (Annex I)',
  annex_ii: 'Annex II',
  labelling: 'Labelling (Annex IIIA)',
  package_leaflet: 'Package leaflet (Annex IIIB)',
};
// EU Product Information reading order: SmPC → Annex II → labelling → leaflet.
const PART_ORDER = ['smpc', 'annex_ii', 'labelling', 'package_leaflet'];

// Stable per-row identity. Prefer the structure-tree ``anchor_id`` (the exact key
// a tree leaf carries, so rail selection and citation landing address the SAME
// node); else fold (document_part, presentation_key, num) for the part-aware
// payload; else the bare ``num`` for the legacy SmPC-only payload.
function sectionKey(s: Section): string {
  if (s.anchor_id) return s.anchor_id;
  return s.document_part
    ? `${s.document_part}|${s.presentation_key || ''}|${s.num}`
    : s.num;
}
interface HL { page: number; box: [number, number, number, number]; kind: string }
// One cited-change span from /compare-block. ``box`` is the word's normalized
// [x0,y0,x1,y1]; absent only for degenerate rows (we then scroll to the page).
interface CitedSpan { page: number; box?: [number, number, number, number] }
interface CompareBlock {
  // Echoed identity — validated against the request before the spans are trusted
  // (a stale/mismatched response must not point the outline at the wrong change).
  drug_id: number; hc_number: string;
  from_version: string; to_version: string; block_id: string;
  section_number: string; section_title: string;
  presentation_key: string | null; presentation_label: string | null;
  document_part?: string | null;   // flag-ON Parser-B path
  operation: string; old_spans: CitedSpan[]; new_spans: CitedSpan[];
}

// Match a bare section string to its diff row — renumber-tolerant (the number
// may appear as the current ``num``, the ``old_num`` or the ``new_num``). Drives
// both the section fallback (block 404) and the block's own row lookup.
export function matchSectionRow(
  sections: Section[], sec?: string | null,
  opts?: { part?: string | null | undefined; presentation?: string | null | undefined },
): Section | null {
  if (!sec) return null;
  const part = opts?.part || null;
  const pres = opts?.presentation || null;
  // Part/presentation-aware match FIRST (a leaflet §4 must never resolve the
  // first SmPC §4; one strength's §4 must not resolve another's). Only fall back
  // to a number-only match when no part/presentation is supplied (legacy).
  const numHit = (x: Section) =>
    x.num === sec || x.old_num === sec || x.new_num === sec;
  if (part || pres) {
    const scoped = sections.find(
      (x) => numHit(x)
        && (!part || x.document_part === part)
        && (!pres || x.presentation_key === pres));
    if (scoped) return scoped;
  }
  return sections.find(numHit) || null;
}

// Where each pane scrolls to for a cited block, and which section row to select.
export interface CompareBlockLanding {
  old: { page: number | null; y: number };
  new: { page: number | null; y: number };
  selected: string | null;
}
// Pure landing math (unit-tested). Prefer the cited change's FIRST span in each
// pane; added-only (no old span) / removed-only (no new span) fall back to the
// matched section row's page + y so both panes still align on the section.
export function planCompareBlockLanding(
  block: Pick<CompareBlock, 'section_number' | 'old_spans' | 'new_spans'>,
  row: Pick<Section, 'num' | 'old_page' | 'new_page' | 'old_y' | 'new_y' | 'anchor_id'
                     | 'document_part' | 'presentation_key'> | null,
): CompareBlockLanding {
  const os = (block.old_spans || []).filter((s) => s && s.page);
  const ns = (block.new_spans || []).filter((s) => s && s.page);
  // Keep page + y from the SAME source: a cited span uses its own page (+ box
  // top, or page-top if boxless); with no span in that pane, fall back to the
  // section row's page + y. Never pair one source's page with another's y.
  return {
    old: os[0] ? { page: os[0].page, y: os[0].box?.[1] ?? 0 }
               : { page: row?.old_page ?? null, y: row?.old_y ?? 0 },
    new: ns[0] ? { page: ns[0].page, y: ns[0].box?.[1] ?? 0 }
               : { page: row?.new_page ?? null, y: row?.new_y ?? 0 },
    // Select by the SAME identity the rail rows use (anchor_id when present, else
    // the part-aware fold, else num) — a bare num never matched the anchor-keyed
    // rows, so the resolved change wasn't highlighted.
    selected: row
      ? (row.anchor_id
         || (row.document_part
             ? `${row.document_part}|${row.presentation_key || ''}|${row.num}`
             : row.num))
      : null,
  };
}
// Segmentation-quality verdict (backend `_section_attribution_status`). When
// 'ambiguous', the per-section breakdown of THIS version pair can't be trusted
// (an older scanned label whose Annex III labelling cartons masquerade as extra
// strengths, or a label with no text layer) — so we fall back to whole-document
// highlights and mark the per-section summaries unavailable.
interface Attribution {
  // 'unassessed' = a legacy row not yet re-checked (fail-closed, like ambiguous).
  status: 'ok' | 'ambiguous' | 'unassessed';
  flags?: string[];
}
interface Diff {
  drug: string;
  old: { vstr: string; date: string; pages: PageInfo[] };
  new: { vstr: string; date: string; pages: PageInfo[] };
  sections: Section[];
  highlights: { old: HL[]; new: HL[] };
  attribution?: Attribution;
  // Parser-B navigation tree (flag-ON payload). ``new`` is the authority for the
  // hierarchical rail; ``old`` is present for reference. Absent on legacy payloads.
  structure?: { old: StructNode[]; new: StructNode[] };
}

// Version label formatting — MUST match EuHistoricalCompareLauncher so the
// in-modal dropdowns read the same "SmPC - V6 - Nov 19 2015" as the launcher.
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtDate(d: string): string {
  const dt = new Date(`${d}T00:00:00`);
  if (isNaN(dt.getTime())) return d;
  return `${MON[dt.getMonth()]} ${String(dt.getDate()).padStart(2, '0')} ${dt.getFullYear()}`;
}

// Clean inline image glyph (Lucide-style) — reads as "figure", not a broken img.
function ImageGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2.5" />
      <circle cx="8.5" cy="8.5" r="1.6" />
      <path d="M21 15.5 16 10.5 5 21.5" />
    </svg>
  );
}

// One clear verdict vocabulary. `full` = the explicit plain-English verdict on
// each section row; `label` = the short word for the count summary + filters.
const VERDICT_META: Record<Verdict, { label: string; full: string; pill: string; count: string; border: string }> = {
  added:      { label: 'Added',      full: 'New section added',        pill: 'bg-emerald-100 text-emerald-700', count: 'text-emerald-600', border: 'border-l-emerald-400' },
  modified:   { label: 'Modified',   full: 'Section content modified', pill: 'bg-amber-100 text-amber-700',     count: 'text-amber-600',   border: 'border-l-amber-400' },
  removed:    { label: 'Deleted',    full: 'Section deleted',          pill: 'bg-rose-100 text-rose-700',       count: 'text-rose-600',    border: 'border-l-rose-400' },
  renumbered: { label: 'Renumbered', full: 'Section renumbered',       pill: 'bg-blue-100 text-blue-700',       count: 'text-blue-600',    border: 'border-l-blue-400' },
  unchanged:  { label: 'Unchanged',  full: 'No change',                pill: '',                                count: 'text-slate-400',   border: 'border-l-transparent' },
};
const VERDICT_ORDER: Verdict[] = ['added', 'modified', 'removed', 'renumbered', 'unchanged'];

// 'figures' is a CONTENT-axis filter (sections whose figures changed), distinct
// from the section VERDICT axis — a figure can be added inside a modified section,
// so it must not be conflated with an "Added section".
type Filter = 'all' | 'figures' | Verdict;
const FILTERS: [Filter, string][] = [
  ['all', 'All'], ['figures', 'Figures'], ['added', 'Added'], ['modified', 'Modified'],
  ['removed', 'Deleted'], ['renumbered', 'Renumbered'], ['unchanged', 'Unchanged'],
];
const hasFigChange = (s: Section) =>
  (s.figures_added || 0) > 0 || (s.figures_removed || 0) > 0;
const LOAD_MSGS = ['Loading versions…', 'Computing changes…', 'Rendering pages…'];

// Plain-English reason a version pair is quarantined, so the caveat is honest
// about WHY the per-section breakdown can't be trusted for this old label.
function quarantineTitle(a?: Attribution): string {
  if (a?.status === 'unassessed') return 'Not yet checked';
  return 'Section attribution uncertain';
}
function quarantineMessage(a?: Attribution): string {
  const flags = a?.flags || [];
  if (a?.status === 'unassessed')
    // legacy row: the segmentation quality of this pair hasn't been verified.
    return 'This version pair hasn’t been re-checked for reliable section attribution yet. Automated per-section change summaries are hidden until it is. Both documents are available below for side-by-side review.';
  if (flags.includes('text_not_extractable'))
    // NOTE: the no-text backend path returns EMPTY highlights — do NOT claim
    // highlights show the differences (they don't).
    return 'This older label has no machine-readable text layer, so changes can’t be attributed to individual sections and automated highlights aren’t available. Both documents remain available below for side-by-side review.';
  return 'This older scanned label stacks per-strength sections that can’t be reliably told apart (its packaging/labelling fields mimic section headings). Section-by-section change counts are unreliable here — rely on the whole-document highlights below, which still show where text was added or removed.';
}

function groupByPage(hls: HL[]): Map<number, HL[]> {
  const m = new Map<number, HL[]>();
  for (const h of hls) { const a = m.get(h.page) || []; a.push(h); m.set(h.page, a); }
  return m;
}

function Pane({ side, pages, hls, cited, show, paneRef, viewing, page, tag, overlay }: {
  side: 'old' | 'new'; pages: PageInfo[]; hls: HL[]; cited: CitedSpan[]; show: boolean;
  paneRef: React.RefObject<HTMLDivElement>; viewing: string; page: number | null; tag: string;
  overlay?: ((page: number) => React.ReactNode) | undefined;
}) {
  const byPage = groupByPage(hls);
  // The cited change's exact spans (from /compare-block) — a distinct indigo
  // outline drawn ABOVE the add/remove wash so the reader's eye lands on the
  // precise words this citation grounds to, not just the section's colour.
  const citedByPage = new Map<number, CitedSpan[]>();
  for (const c of cited) {
    if (!c.box) continue;
    const a = citedByPage.get(c.page) || []; a.push(c); citedByPage.set(c.page, a);
  }
  return (
    <div ref={paneRef} className="relative flex-1 overflow-y-auto bg-slate-100" style={{ minWidth: 0 }}>
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-slate-200 px-4 py-1.5 text-[11px] flex justify-between gap-2 items-center">
        <span className="flex items-center gap-2 shrink-0">
          <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${side === 'old' ? 'bg-slate-100 text-slate-500' : 'bg-indigo-100 text-indigo-700'}`}>
            {side === 'old' ? 'Older' : 'Newer'}
          </span>
          {tag && <span className="font-medium text-slate-700">{tag}</span>}
        </span>
        <span className="truncate text-slate-500">{viewing ? `${viewing}${page ? ` · p${page}` : ''}` : ''}</span>
      </div>
      <div className="px-4 py-3">
        {pages.map((p) => (
          <div key={p.page} data-page={p.page} className="relative mx-auto mb-4 rounded-lg shadow-sm ring-1 ring-slate-200 bg-white"
               style={{ width: '100%', maxWidth: 700, aspectRatio: `${p.w} / ${p.h}` }}>
            <img src={p.img} loading="lazy" alt={`page ${p.page}`} className="block w-full h-full select-none rounded-lg" draggable={false} />
            {show && (byPage.get(p.page) || []).map((h, i) => {
              const isImg = h.kind === 'img_add' || h.kind === 'img_del';
              const isAdd = h.kind === 'add' || h.kind === 'img_add';
              const isMove = h.kind === 'move';   // same text, new position within the section
              return (
                <div key={i} className="absolute pointer-events-none" style={{
                  left: `${h.box[0] * 100}%`, top: `${h.box[1] * 100}%`,
                  width: `${(h.box[2] - h.box[0]) * 100}%`, height: `${(h.box[3] - h.box[1]) * 100}%`,
                  backgroundColor: isImg ? (isAdd ? 'rgba(34,197,94,0.10)' : 'rgba(239,68,68,0.10)')
                    : (isMove ? 'rgba(59,130,246,0.30)' : isAdd ? 'rgba(34,197,94,0.30)' : 'rgba(239,68,68,0.30)'),
                  border: isImg ? `2px dashed ${isAdd ? 'rgba(22,163,74,0.9)' : 'rgba(220,38,38,0.9)'}` : undefined,
                  boxShadow: isImg ? undefined
                    : (isMove ? 'inset 0 0 0 1px rgba(37,99,235,0.65)'
                      : isAdd ? 'inset 0 0 0 1px rgba(22,163,74,0.65)' : 'inset 0 0 0 1px rgba(220,38,38,0.65)'),
                  borderRadius: isImg ? 4 : 2,
                }}>
                  {isImg && (
                    <span className={`absolute -top-3 left-0 flex items-center gap-1 text-[10px] font-medium leading-none px-1.5 py-1 rounded-full shadow-sm text-white ${isAdd ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                      <ImageGlyph className="w-3 h-3" /> {isAdd ? 'Figure added' : 'Figure removed'}
                    </span>
                  )}
                </div>
              );
            })}
            {show && (citedByPage.get(p.page) || []).map((c, i) => (
              // Static outline (no pulse — a cited change can span hundreds of
              // word boxes). Only drawn in "Changes" mode, alongside the diff wash.
              <div key={`cited-${i}`} data-testid="cited-span" className="absolute pointer-events-none" style={{
                left: `${c.box![0] * 100}%`, top: `${c.box![1] * 100}%`,
                width: `${(c.box![2] - c.box![0]) * 100}%`, height: `${(c.box![3] - c.box![1]) * 100}%`,
                border: '2px solid rgba(79,70,229,0.95)', borderRadius: 3,
                boxShadow: '0 0 0 2px rgba(79,70,229,0.25)', backgroundColor: 'rgba(99,102,241,0.12)',
              }} />
            ))}
            {overlay?.(p.page)}
            <div className="absolute top-1 right-2 text-[10px] text-slate-400">p{p.page}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Two-pane skeleton shown while a pair loads (cold render can take a few
// seconds) so it reads as working, not laggy.
function CompareSkeleton({ msg }: { msg: string }) {
  return (
    <div className="relative flex flex-1 min-h-0">
      <div className="flex flex-1 min-h-0 animate-pulse">
        <div className="w-[380px] shrink-0 border-r bg-white p-3 space-y-2">
          <div className="h-6 bg-slate-100 rounded" />
          <div className="h-6 bg-slate-100 rounded w-2/3" />
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="h-6 bg-gray-100 rounded" />
          ))}
        </div>
        {['old', 'new'].map((s) => (
          <div key={s} className="flex-1 bg-gray-100 p-4 space-y-4 overflow-hidden">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="mx-auto bg-white rounded shadow"
                   style={{ width: '100%', maxWidth: 700, aspectRatio: '0.72' }} />
            ))}
          </div>
        ))}
      </div>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="flex items-center gap-3 bg-white/95 border rounded-lg px-5 py-3 shadow-sm">
          <span className="inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-600">{msg}</span>
        </div>
      </div>
    </div>
  );
}

function SectionRow({ s, isSel, onSelect }: {
  s: Section; isSel: boolean; onSelect: (s: Section) => void;
}) {
  const m = VERDICT_META[s.verdict];
  const changed = s.verdict !== 'unchanged';
  return (
    <div className={`border-b border-slate-50 border-l-2 ${m.border} transition-colors ${isSel ? 'bg-indigo-50/70' : 'hover:bg-slate-50'}`}>
      <button onClick={() => onSelect(s)} className="w-full flex items-center gap-2 px-3 py-2 text-left">
        <span className="text-[11px] tabular-nums w-9 shrink-0 text-slate-400">{s.num}</span>
        <span className={`flex-1 truncate ${changed ? 'text-slate-700' : 'text-slate-400'}`}>{s.title}</span>
        {changed && (s.words_added > 0 || s.words_removed > 0 || (s.words_moved || 0) > 0) && (
          <span className="text-[10px] tabular-nums shrink-0">
            {s.words_added > 0 && <span className="text-emerald-600">+{s.words_added}</span>}
            {s.words_removed > 0 && <span className="text-rose-500 ml-1">−{s.words_removed}</span>}
            {(s.words_moved || 0) > 0 && <span className="text-blue-600 ml-1">↔{s.words_moved}</span>}
          </span>
        )}
        {changed && ((s.figures_added || 0) > 0 || (s.figures_removed || 0) > 0) && (
          <span className="flex items-center gap-0.5 text-[10px] tabular-nums shrink-0" title="figures added / removed">
            <ImageGlyph className="w-3 h-3 text-slate-400" />
            {(s.figures_added || 0) > 0 && <span className="text-emerald-600">+{s.figures_added}</span>}
            {(s.figures_removed || 0) > 0 && <span className="text-rose-500 ml-0.5">−{s.figures_removed}</span>}
          </span>
        )}
        {/* Only badge the notable verdicts (added / deleted / renumbered).
            'Modified' is the common case — the +/- word counts already say it
            changed, so a pill on every row would just be noise. */}
        {(s.verdict === 'added' || s.verdict === 'removed' || s.verdict === 'renumbered') &&
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 whitespace-nowrap ${m.pill}`}>{m.full}</span>}
      </button>
      {isSel && changed && (
        <div className="px-3 pb-3 pt-0.5 text-[11px] space-y-1">
          {s.summary_add && <div className="text-emerald-700 truncate">+ {s.summary_add}</div>}
          {s.summary_del && <div className="text-rose-700 truncate line-through decoration-rose-300">− {s.summary_del}</div>}
          <div className="text-slate-500">
            Pages <b className="text-slate-700">p{s.old_page ?? '—'}</b> → <b className="text-slate-700">p{s.new_page ?? '—'}</b>
            {s.verdict === 'renumbered' && <span className="text-blue-600 ml-2">§{s.old_num} → §{s.new_num}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// Another label family reusing this compare (de_pdf): its own endpoints, labels
// and an optional per-pane side panel (the German compare's English panel).
// Absent = the EU defaults below, so the EU compare is unchanged.
export interface CompareSource {
  versionsPath: string;
  diffPath: (oldV: string, newV: string) => string;
  docLabel: string;     // "SmPC" / "PIL" in the version tags
  subtitle: string;
  // Drawn inside each page box (e.g. hover-translate boxes).
  pageOverlay?: (side: 'old' | 'new', vstr: string, page: number) => React.ReactNode;
}

export default function EuHistoricalCompare({
  hc, brand, onClose, initialOld, initialNew,
  drugId, initialSection, initialPresentation, initialBlockId, initialDocumentPart, source,
}: {
  hc: string; brand: string; onClose?: () => void;
  source?: CompareSource | undefined;
  initialOld?: string; initialNew?: string;
  // Chat-citation landing target: resolve ``initialBlockId`` to its exact spans
  // (needs ``drugId`` for the server-side hc gate) and outline them; fall back to
  // ``initialSection`` on 404. ``initialPresentation`` is carried for URL
  // fidelity — the block's spans already encode the strength.
  drugId?: number | undefined; initialSection?: string | undefined;
  initialPresentation?: string | undefined; initialBlockId?: string | undefined;
  // Flag-ON Parser-B path: which document PART the cited block belongs to. The
  // block_id is part-namespaced server-side, so the compare-block request MUST
  // carry it (a SmPC §4 id must never resolve a leaflet §4 block). Absent on the
  // legacy SmPC-only path → the server resolves against the legacy table.
  initialDocumentPart?: string | undefined;
}) {
  const [versions, setVersions] = useState<Ver[]>([]);
  const [oldV, setOldV] = useState('');
  const [newV, setNewV] = useState('');
  const [diff, setDiff] = useState<Diff | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [selected, setSelected] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [showChanges, setShowChanges] = useState(true);
  const [syncScroll, setSyncScroll] = useState(true);
  const [loadStage, setLoadStage] = useState(0);
  // Cited-change outline spans (from /compare-block) for each pane. Cleared when
  // the pair changes so a stale outline never lingers on an unrelated diff.
  const [citedOld, setCitedOld] = useState<CitedSpan[]>([]);
  const [citedNew, setCitedNew] = useState<CitedSpan[]>([]);
  // Active figure index within the selected section's fig_marks (-1 = none).
  const [figIdx, setFigIdx] = useState(-1);
  const landedKey = useRef('');   // one-shot guard per (pair, target)

  const oldPane = useRef<HTMLDivElement>(null);
  const newPane = useRef<HTMLDivElement>(null);
  // Sync-scroll ANCHOR: the two pane tops captured at the last deliberate landing
  // (section/change/citation/sync-on). Free-scroll then drives the other pane by
  // pixel-delta FROM this pair, so alignment holds even when the versions differ
  // in length. suspendSync mutes the driver during a programmatic smooth-scroll so
  // the two animations don't fight; landBoth lifts it once both panes stop moving.
  const anchor = useRef<{ old: number; new: number } | null>(null);
  const suspendSync = useRef(false);
  const suspendTimer = useRef<number>(0);

  useEffect(() => {
    if (!hc) return;
    setDiff(null);
    apiGet<{ versions: Ver[] }>(source?.versionsPath ?? `/eu-compare-proto/versions?hc=${encodeURIComponent(hc)}`).then((r) => {
      setVersions(r.versions);
      const has = (v?: string) => !!v && r.versions.some((x) => x.vstr === v);
      const first = r.versions[0]?.vstr; const last = r.versions[r.versions.length - 1]?.vstr;
      const o = has(initialOld) ? initialOld! : first;
      const n = has(initialNew) ? initialNew! : last;
      if (o && n && o !== n) { setOldV(o); setNewV(n); }
      else { setOldV(''); setNewV(''); }
    }).catch((e) => setErr(String(e)));
  }, [hc, initialOld, initialNew, source?.versionsPath]);

  const loadDiff = useCallback(async (o: string, n: string) => {
    if (!hc || !o || !n) return;
    setLoading(true); setErr(''); setDiff(null); setSelected('');
    setCitedOld([]); setCitedNew([]);
    try {
      const d = await apiGet<Diff>(source ? source.diffPath(o, n)
        : `/eu-compare-proto/diff?hc=${encodeURIComponent(hc)}`
        + `&name=${encodeURIComponent(brand)}&old=${encodeURIComponent(o)}&new=${encodeURIComponent(n)}`);
      setDiff(d);
    } catch (e) { setErr(String(e)); } finally { setLoading(false); }
  }, [hc, brand, source]);
  useEffect(() => { if (hc && oldV && newV) loadDiff(oldV, newV); }, [hc, oldV, newV, loadDiff]);
  useEffect(() => {
    if (!loading) { setLoadStage(0); return; }
    const t = setInterval(
      () => setLoadStage((s) => Math.min(s + 1, LOAD_MSGS.length - 1)), 2500);
    return () => clearInterval(t);
  }, [loading]);

  // Absolute scroll top for (page, y) in a pane, or null if that page isn't laid
  // out (e.g. an added-only section has no old page). ``-44`` leaves the heading a
  // little below the pane top instead of flush against it.
  const computeTarget = useCallback(
    (ref: React.RefObject<HTMLDivElement>, page: number | null, y: number | null): number | null => {
      if (!ref.current || !page) return null;
      const el = ref.current.querySelector<HTMLElement>(`[data-page="${page}"]`);
      if (!el) return null;
      // Clamp to the pane's max scroll: the browser stops there anyway, and the sync
      // anchor must equal where the pane actually lands — an unclamped anchor makes
      // the driver drag the other pane back by the overshoot once suspend lifts.
      return Math.max(0, Math.min(ref.current.scrollHeight - ref.current.clientHeight,
        el.offsetTop + (y || 0) * el.clientHeight - 44));
    }, []);
  // A deliberate landing: scroll BOTH panes to their own page+y AND capture those
  // target tops as the sync anchor, so subsequent free-scroll holds this exact
  // alignment. Every section/change/citation landing calls this → drift resets.
  const landBoth = useCallback(
    (op: number | null, oy: number | null, np: number | null, ny: number | null) => {
      const a = oldPane.current, b = newPane.current;
      const ot = computeTarget(oldPane, op, oy);
      const nt = computeTarget(newPane, np, ny);
      suspendSync.current = true;   // mute the driver while both animate to target
      if (a && ot != null) a.scrollTo({ top: ot, behavior: 'smooth' });
      if (b && nt != null) b.scrollTo({ top: nt, behavior: 'smooth' });
      anchor.current = {
        old: ot != null ? ot : (a?.scrollTop ?? 0),
        new: nt != null ? nt : (b?.scrollTop ?? 0),
      };
      window.clearTimeout(suspendTimer.current);
      // Keep the driver muted until BOTH panes have stopped moving, then re-anchor on
      // where they actually rest. A fixed 550 ms delay lifted it mid-animation on long
      // jumps, and the driver's first write cancelled both smooth scrolls halfway.
      // Re-anchoring on the resting positions also covers a user-interrupted landing.
      let lastOld = a?.scrollTop ?? 0, lastNew = b?.scrollTop ?? 0, still = 0, waited = 0;
      const settle = () => {
        const curOld = a?.scrollTop ?? 0, curNew = b?.scrollTop ?? 0;
        still = curOld === lastOld && curNew === lastNew ? still + 1 : 0;
        lastOld = curOld; lastNew = curNew; waited += 50;
        if (still < 3 && waited < 3000) { suspendTimer.current = window.setTimeout(settle, 50); return; }
        anchor.current = { old: curOld, new: curNew };
        suspendSync.current = false;
      };
      suspendTimer.current = window.setTimeout(settle, 50);
    }, [computeTarget]);
  // Land both panes on the i-th changed figure of section ``s`` and outline it.
  // added → new pane on the figure (old pane on the section); removed → old pane on
  // the figure (new pane on the section). Sets the active figure index.
  const landFigure = (s: Section, i: number) => {
    const marks = s.fig_marks || [];
    if (!marks.length) { setFigIdx(-1); landBoth(s.old_page, s.old_y, s.new_page, s.new_y); return; }
    const n = marks.length;
    const j = ((i % n) + n) % n;
    const m = marks[j]!;
    setFigIdx(j);
    const span: CitedSpan[] = [{ page: m.page, box: m.box }];
    if (m.op === 'removed') {
      setCitedOld(span); setCitedNew([]);
      landBoth(m.page, m.y, s.new_page, s.new_y);
    } else {
      setCitedNew(span); setCitedOld([]);
      landBoth(s.old_page, s.old_y, m.page, m.y);
    }
  };
  const select = (s: Section) => {
    setSelected(sectionKey(s));
    setCitedOld([]); setCitedNew([]);   // clear any prior change/citation outline
    // In the Figures filter, a row-click lands on the section's FIRST changed
    // figure (outlined); use the figure stepper to walk the rest. Other filters
    // land on the section heading.
    if (filter === 'figures' && hasFigChange(s) && (s.fig_marks?.length)) {
      landFigure(s, 0);
    } else {
      setFigIdx(-1);
      landBoth(s.old_page, s.old_y, s.new_page, s.new_y);
    }
  };

  // Chat-citation landing (senior Item 3/4/5). After the diff for the DEEP-LINKED
  // pair loads, resolve the cited block to its exact old/new spans and outline +
  // scroll to them. Fail-closed: any non-2xx (404 fabricated/ambiguous/immature
  // block, or a network error) falls back QUIETLY to landing on the section — no
  // outline, no error surfaced. One-shot per (pair, target); only fires for the
  // originally-cited pair so manual dropdown changes just clear the outline.
  useEffect(() => {
    if (!diff) return;
    const oVstr = diff.old.vstr, nVstr = diff.new.vstr;
    const key = `${oVstr}|${nVstr}|${initialBlockId || ''}|${initialSection || ''}`
      + `|${initialPresentation || ''}|${initialDocumentPart || ''}`;
    if (landedKey.current === key) return;
    landedKey.current = key;

    // NEVER land on a quarantined pair's geometry: its section attribution is
    // untrusted ('ambiguous') or unverified ('unassessed'). No block outline, no
    // section scroll — the whole-document compare stands alone (fail-closed).
    if (diff.attribution?.status === 'ambiguous'
        || diff.attribution?.status === 'unassessed') return;

    // Only land for the pair the citation actually named; a user-driven dropdown
    // change to a different pair should not re-run the citation landing.
    const isDeepLinkPair = oVstr === initialOld && nVstr === initialNew;
    if (!isDeepLinkPair || (!initialBlockId && !initialSection)) return;

    const secRow = (sec?: string | null) => matchSectionRow(
      diff.sections || [], sec,
      { part: initialDocumentPart, presentation: initialPresentation });
    const landOnSection = (sec?: string | null) => {
      const s = secRow(sec);
      if (s) select(s);
    };

    // FIGURE citation ('fig:…' id): there is no text block to fetch — land both
    // panes on the cited section's changed figure (the img_add/img_del overlay
    // marks it), NOT the section heading. Falls back to the section position on the
    // side that has no figure change (added-only / removed-only).
    if (initialBlockId && initialBlockId.startsWith('fig:')) {
      const s = secRow(initialSection);
      if (s) {
        setSelected(sectionKey(s));
        landFigure(s, 0);   // land on the section's first changed figure, outlined
      }
      return;
    }

    if (initialBlockId && drugId != null) {
      let alive = true;
      (async () => {
        try {
          const b = await apiGet<CompareBlock>(
            `/eu-history/drugs/${drugId}/compare-block`
            + `?from=${encodeURIComponent(oVstr)}&to=${encodeURIComponent(nVstr)}`
            + `&block_id=${encodeURIComponent(initialBlockId)}`
            // part-namespaces the block id (flag-ON Parser-B path); omitted →
            // server resolves against the legacy SmPC-only table.
            + (initialDocumentPart
                ? `&document_part=${encodeURIComponent(initialDocumentPart)}` : ''));
          if (!alive) return;
          // Validate the echoed identity before trusting the spans: the block
          // MUST be for this drug + this pair + this exact block, and — when the
          // citation named a strength — that presentation. Any mismatch → treat
          // as a miss and degrade to the section landing (never outline a wrong
          // change from a stale/misrouted response).
          const echoOk =
            b.drug_id === drugId &&
            b.from_version === oVstr && b.to_version === nVstr &&
            b.block_id === initialBlockId &&
            (!initialPresentation || b.presentation_key === initialPresentation);
          if (!echoOk) { landOnSection(initialSection); return; }
          setCitedOld((b.old_spans || []).filter((s) => s && s.page));
          setCitedNew((b.new_spans || []).filter((s) => s && s.page));
          // resolve the rail row by the ECHOED block's own full identity so a
          // multi-presentation §N selects the right strength's row.
          const blockRow = matchSectionRow(
            diff.sections || [], b.section_number,
            { part: b.document_part || initialDocumentPart,
              presentation: b.presentation_key || initialPresentation });
          const land = planCompareBlockLanding(b, blockRow);
          if (land.selected) setSelected(land.selected);
          landBoth(land.old.page, land.old.y, land.new.page, land.new.y);
        } catch {
          if (alive) landOnSection(initialSection);   // 404 → quiet section landing
        }
      })();
      return () => { alive = false; };
    }
    landOnSection(initialSection);
    return;
  }, [diff, initialBlockId, initialSection, initialPresentation, initialDocumentPart, initialOld, initialNew, drugId]);

  // Sections in document (template) order — the backend already returns them so.
  const sections = diff?.sections || [];
  // Quarantine: this pair's per-section attribution can't be trusted — either
  // assessed 'ambiguous' (labelling-carton leak / no text layer) OR 'unassessed'
  // (a legacy row not yet re-checked). BOTH fail closed: the whole section rail
  // is suppressed so the UI never presents unverified section data as if checked.
  const quarantined = diff?.attribution?.status === 'ambiguous'
    || diff?.attribution?.status === 'unassessed';
  const counts = useMemo(() => {
    const c: Record<Verdict, number> = { added: 0, modified: 0, removed: 0, renumbered: 0, unchanged: 0 };
    for (const s of sections) c[s.verdict]++;
    return c;
  }, [sections]);
  // Figure totals across ALL parts (content axis, separate from section verdicts):
  // ``sections`` = rows with any figure change, ``added``/``removed`` = figure sums.
  const figTotals = useMemo(() => {
    let sec = 0, added = 0, removed = 0;
    for (const s of sections) {
      const a = s.figures_added || 0, r = s.figures_removed || 0;
      if (a > 0 || r > 0) sec++;
      added += a; removed += r;
    }
    return { sections: sec, added, removed };
  }, [sections]);
  const visible = useMemo(
    () => sections.filter((s) =>
      filter === 'all' ? true
        : filter === 'figures' ? hasFigChange(s)
          : s.verdict === filter),
    [sections, filter]);
  const changed = useMemo(() => sections.filter((s) => s.verdict !== 'unchanged'), [sections]);

  // ── structure-tree navigation model (the flag-ON hierarchical rail) ──────────
  // Join each tree leaf to its diff row by anchor_id; build the parent→children
  // forest; group roots by document_part (SmPC / Annex II / Labelling / Leaflet).
  const rowByAnchor = useMemo(() => {
    const m = new Map<string, Section>();
    for (const s of sections) if (s.anchor_id) m.set(s.anchor_id, s);
    return m;
  }, [sections]);
  const tree = useMemo(() => {
    const nodes = diff?.structure?.new || [];
    const kids = new Map<string, StructNode[]>();
    const roots: StructNode[] = [];
    for (const n of nodes) {
      if (n.parent_anchor_id) {
        const a = kids.get(n.parent_anchor_id) || []; a.push(n); kids.set(n.parent_anchor_id, a);
      } else roots.push(n);
    }
    // stable child order = reading order (start_page, start_y)
    for (const arr of kids.values())
      arr.sort((a, b) => (a.start_page ?? 0) - (b.start_page ?? 0)
                          || (a.start_y ?? 0) - (b.start_y ?? 0));
    const byPart = new Map<string, StructNode[]>();
    for (const r of roots) {
      const a = byPart.get(r.document_part) || []; a.push(r); byPart.set(r.document_part, a);
    }
    return { nodes, kids, byPart, has: nodes.length > 0 };
  }, [diff]);
  // Filter → the set of anchors to render: matching section leaves PLUS every
  // ancestor group, so the hierarchy above a match is retained (never orphan a
  // change under a hidden presentation).
  const visibleAnchors = useMemo(() => {
    if (filter === 'all' || !tree.has) return null;   // null = show everything
    const parentOf = new Map<string, string | null>();
    for (const n of tree.nodes) parentOf.set(n.anchor_id, n.parent_anchor_id);
    const match = (row: Section) =>
      filter === 'figures' ? hasFigChange(row) : row.verdict === filter;
    const keep = new Set<string>();
    for (const n of tree.nodes) {
      const row = rowByAnchor.get(n.anchor_id);
      if (row && match(row)) {
        let a: string | null = n.anchor_id;
        while (a && !keep.has(a)) { keep.add(a); a = parentOf.get(a) ?? null; }
      }
    }
    return keep;
  }, [filter, tree, rowByAnchor]);

  const step = useCallback((dir: 1 | -1) => {
    if (!changed.length) return;
    const i = changed.findIndex((s) => sectionKey(s) === selected);
    const nx = changed[(i + dir + changed.length) % changed.length];
    if (nx) select(nx);
  }, [changed, selected]);
  // A compare left open in a hidden shell tab must not step on the active
  // tab's keystrokes: listen only while this tab is the visible one.
  const { isActive } = useTab();
  useEffect(() => {
    if (!isActive) return undefined;
    const h = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'SELECT' || tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowRight' || e.key === 'j') step(1);
      if (e.key === 'ArrowLeft' || e.key === 'k') step(-1);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [step, isActive]);

  // Sync scroll (ANCHORED): scrolling one pane drives the other by the SAME PIXEL
  // DELTA measured from the last deliberate landing's anchor pair — NOT the whole-
  // document ratio, which drifts the instant the two versions differ in length
  // (added/removed pages). Both panes render at the same width, so a pixel delta
  // ≈ the same physical distance. Every section-click / citation landing (and
  // enabling sync) resets the anchor, so drift can't accumulate. The ignore
  // counters swallow the synthetic scroll we cause, so there's no feedback loop.
  useEffect(() => {
    if (!syncScroll || !diff) return;
    const a = oldPane.current, b = newPane.current;
    if (!a || !b) return;
    if (!anchor.current) anchor.current = { old: a.scrollTop, new: b.scrollTop };
    const ignore = { a: 0, b: 0 };
    const drive = (src: HTMLDivElement, dst: HTMLDivElement,
                   srcSide: 'old' | 'new', dstSide: 'old' | 'new', dstKey: 'a' | 'b') => {
      if (suspendSync.current || !anchor.current) return;
      const delta = src.scrollTop - anchor.current[srcSide];
      const target = Math.max(
        0, Math.min(anchor.current[dstSide] + delta, dst.scrollHeight - dst.clientHeight));
      if (Math.abs(dst.scrollTop - target) > 1) { ignore[dstKey]++; dst.scrollTop = target; }
    };
    const onA = () => { if (ignore.a > 0) { ignore.a--; return; } drive(a, b, 'old', 'new', 'b'); };
    const onB = () => { if (ignore.b > 0) { ignore.b--; return; } drive(b, a, 'new', 'old', 'a'); };
    a.addEventListener('scroll', onA, { passive: true });
    b.addEventListener('scroll', onB, { passive: true });
    return () => { a.removeEventListener('scroll', onA); b.removeEventListener('scroll', onB); };
  }, [syncScroll, diff]);
  // A new version pair invalidates the old anchor — clear it so the sync effect
  // re-seeds from the fresh panes instead of driving off stale positions.
  useEffect(() => {
    anchor.current = null;
    // Stop a pending landing's settle poll when the pair changes or we unmount.
    return () => { window.clearTimeout(suspendTimer.current); suspendSync.current = false; };
  }, [diff]);

  const sel = sections.find((s) => sectionKey(s) === selected) || null;

  // Walk through the selected section's changed figures one at a time. From "no
  // figure active" (-1), forward lands on the first and backward on the last;
  // landFigure wraps the index so the stepper is circular.
  const stepFig = (dir: 1 | -1) => {
    const marks = sel?.fig_marks || [];
    if (!sel || !marks.length) return;
    landFigure(sel, figIdx < 0 ? (dir === 1 ? 0 : marks.length - 1) : figIdx + dir);
  };

  // ── hierarchical rail renderer (part → presentation → section → subsection) ──
  const toggle = (a: string) => setCollapsed((prev) => {
    const n = new Set(prev);
    if (n.has(a)) n.delete(a); else n.add(a);
    return n;
  });
  const scrollToNode = (node: StructNode) => {
    setSelected(node.anchor_id);
    setFigIdx(-1);
    setCitedOld([]); setCitedNew([]);
    // group headers only carry the new-tree start; land both panes there + anchor.
    landBoth(node.start_page ?? null, node.start_y ?? 0,
             node.start_page ?? null, node.start_y ?? 0);
  };
  // strip a leading part prefix ("SmPC: CellCept 250 mg" → "CellCept 250 mg")
  // since the part is already the group header above it.
  const stripPartPrefix = (s: string) =>
    s.replace(/^(SmPC|PIL|Package Leaflet|Labelling|Annex[^:]*)\s*:\s*/i, '');

  const renderNode = (node: StructNode, depth: number): React.ReactNode => {
    if (visibleAnchors && !visibleAnchors.has(node.anchor_id)) return null;
    const children = tree.kids.get(node.anchor_id) || [];
    if (node.is_group) {
      const open = !collapsed.has(node.anchor_id);
      const raw = node.presentation_label || node.section_title
        || PART_LABELS[node.document_part] || node.node_kind || '';
      return (
        <div key={node.anchor_id}>
          <button onClick={() => toggle(node.anchor_id)}
                  style={{ paddingLeft: 8 + depth * 14 }}
                  className="w-full flex items-center gap-1.5 pr-2 py-1.5 text-left text-[12px] font-semibold text-slate-600 hover:bg-slate-50 border-b border-slate-50">
            <span className={`shrink-0 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}>›</span>
            <span className="truncate">{stripPartPrefix(raw)}</span>
          </button>
          {open && children.map((c) => renderNode(c, depth + 1))}
        </div>
      );
    }
    const row = rowByAnchor.get(node.anchor_id);
    const v = row?.verdict ?? 'unchanged';
    const m = VERDICT_META[v];
    const isSel = selected === node.anchor_id;
    const num = node.section_number || row?.num || '';
    const title = node.section_title || row?.title || '';
    const chg = v !== 'unchanged';
    return (
      <div key={node.anchor_id}>
        <div className={`border-l-2 ${m.border} ${isSel ? 'bg-indigo-50/70' : 'hover:bg-slate-50'}`}>
          <button onClick={() => (row ? select(row) : scrollToNode(node))}
                  style={{ paddingLeft: 8 + depth * 14 }}
                  className="w-full flex items-center gap-2 pr-3 py-1.5 text-left">
            <span className="text-[11px] tabular-nums w-9 shrink-0 text-slate-400">{num}</span>
            <span className={`flex-1 truncate text-[13px] ${chg ? 'text-slate-700' : 'text-slate-400'}`}>{title}</span>
            {chg && row && (row.words_added > 0 || row.words_removed > 0 || (row.words_moved || 0) > 0) && (
              <span className="text-[10px] tabular-nums shrink-0">
                {row.words_added > 0 && <span className="text-emerald-600">+{row.words_added}</span>}
                {row.words_removed > 0 && <span className="text-rose-500 ml-1">−{row.words_removed}</span>}
                {(row.words_moved || 0) > 0 && <span className="text-blue-600 ml-1">↔{row.words_moved}</span>}
              </span>
            )}
            {chg && row && ((row.figures_added || 0) > 0 || (row.figures_removed || 0) > 0) && (
              <span className="flex items-center gap-0.5 text-[10px] tabular-nums shrink-0" title="figures added / removed">
                <ImageGlyph className="w-3 h-3 text-slate-400" />
                {(row.figures_added || 0) > 0 && <span className="text-emerald-600">+{row.figures_added}</span>}
                {(row.figures_removed || 0) > 0 && <span className="text-rose-500 ml-0.5">−{row.figures_removed}</span>}
              </span>
            )}
            {(v === 'added' || v === 'removed' || v === 'renumbered') &&
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 whitespace-nowrap ${m.pill}`}>{m.full}</span>}
          </button>
        </div>
        {children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  // Version dropdown options — newest-first, "SmPC - V{n} - date" (chrono
  // ordinal, oldest=V1), identical to the launcher for consistency.
  const docLabel = source?.docLabel ?? 'SmPC';
  const verOpts = versions
    .map((v, i) => ({ vstr: v.vstr, idx: i, label: `${docLabel} - V${i + 1} - ${fmtDate(v.date)}` }))
    .reverse();
  // Gate each dropdown so Older (left) is always older than Newer (right).
  const oldIdx = versions.findIndex((v) => v.vstr === oldV);
  const newIdx = versions.findIndex((v) => v.vstr === newV);
  const oldTag = oldIdx >= 0 ? `${docLabel} V${oldIdx + 1} · ${fmtDate(versions[oldIdx]!.date)}` : '';
  const newTag = newIdx >= 0 ? `${docLabel} V${newIdx + 1} · ${fmtDate(versions[newIdx]!.date)}` : '';

  return (
    <div className="flex flex-col h-full text-sm">
      <div className="flex items-center gap-3 px-5 py-2.5 border-b border-slate-200 bg-white shrink-0">
        <span className="font-semibold text-slate-800 truncate">
          {brand}<span className="font-normal text-slate-400"> · {source?.subtitle ?? 'historical EU Product Information'}</span>
        </span>
        <span title="Beta: EU versions are still being validated against the EMA / EC sources" className="rounded-ctl border border-signal/30 px-1.5 font-mono text-[10px] tracking-[0.08em] text-signal shrink-0">BETA</span>
        {versions.length > 0 && (
          <div className="flex items-center gap-2 ml-1">
            <select className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-slate-700 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-colors"
                    value={oldV} onChange={(e) => setOldV(e.target.value)}>
              {verOpts.map((o) => (
                <option key={o.vstr} value={o.vstr} disabled={newIdx >= 0 && o.idx >= newIdx}>{o.label}</option>
              ))}
            </select>
            <span className="text-slate-300">→</span>
            <select className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-slate-700 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-colors"
                    value={newV} onChange={(e) => setNewV(e.target.value)}>
              {verOpts.map((o) => (
                <option key={o.vstr} value={o.vstr} disabled={oldIdx >= 0 && o.idx <= oldIdx}>{o.label}</option>
              ))}
            </select>
          </div>
        )}
        <div className="ml-auto flex items-center gap-3">
          {showChanges && (
            <div className="hidden md:flex items-center gap-3 text-[11px] text-slate-500">
              <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-400/60 ring-1 ring-emerald-500" /> Added in newer</span>
              <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-rose-400/60 ring-1 ring-rose-500" /> Removed from older</span>
              <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-blue-400/60 ring-1 ring-blue-500" /> Moved within section</span>
            </div>
          )}
          {/* Figure stepper: when the selected section has changed figures, walk
              through each one (◀ ▶). Lands both panes on the figure and outlines
              it; the count reflects added + removed figures in that section. */}
          {sel && sel.fig_marks && sel.fig_marks.length > 0 && (
            <div className="flex items-center gap-1 rounded-lg bg-amber-50 ring-1 ring-amber-200 px-1.5 py-1 text-xs text-amber-800"
                 title="Step through each changed figure in this section">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" />
              </svg>
              <button onClick={() => stepFig(-1)} className="px-1 rounded hover:bg-amber-100" title="Previous figure">◀</button>
              <span className="tabular-nums select-none">
                {figIdx < 0 ? `${sel.fig_marks.length} fig` : `${figIdx + 1} / ${sel.fig_marks.length}`}
              </span>
              <button onClick={() => stepFig(1)} className="px-1 rounded hover:bg-amber-100" title="Next figure">▶</button>
            </div>
          )}
          {/* Sync scroll: keep both PDFs aligned while free-scrolling. Enabling it
              anchors on the CURRENT positions so it holds where you already are. */}
          <button onClick={() => setSyncScroll((v) => {
                    const nv = !v;
                    if (nv && oldPane.current && newPane.current)
                      anchor.current = { old: oldPane.current.scrollTop, new: newPane.current.scrollTop };
                    return nv;
                  })}
                  title="Scroll both documents together, anchored where you are"
                  className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs transition-colors ${syncScroll
                    ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200'
                    : 'bg-slate-100 text-slate-500 hover:text-slate-700'}`}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M7 8l-4 4 4 4" /><path d="M17 8l4 4-4 4" /><line x1="3" y1="12" x2="21" y2="12" />
            </svg>
            Sync scroll
          </button>
          <div className="flex rounded-lg bg-slate-100 p-0.5 text-xs">
            <button onClick={() => setShowChanges(false)} className={`rounded-md px-3 py-1 transition-colors ${!showChanges ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Document</button>
            <button onClick={() => setShowChanges(true)} className={`rounded-md px-3 py-1 transition-colors ${showChanges ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Changes</button>
          </div>
          {onClose && <button onClick={onClose} className="rounded-md w-7 h-7 flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors text-lg leading-none" title="Close">×</button>}
        </div>
      </div>

      {err && <div className="px-5 py-2 text-rose-600 bg-rose-50 shrink-0">{err}</div>}
      {loading && <CompareSkeleton msg={LOAD_MSGS[loadStage] || LOAD_MSGS[0]!} />}

      {diff && (
        <div className="flex flex-1 min-h-0">
          <div className="w-[380px] shrink-0 flex flex-col border-r border-slate-200 bg-white">
            {quarantined ? (
              /* Quarantined pair: the per-section attribution can't be trusted,
                 so the ENTIRE section rail (counts, filters, rows, next/prev) is
                 suppressed — never present unverified section data as if checked.
                 Only the two PDFs + page nav remain (in the panes to the right). */
              <div className="flex-1 overflow-y-auto px-4 py-5 text-[13px] leading-relaxed text-amber-900">
                <div className="flex items-center gap-2 font-semibold text-amber-700 mb-2">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  {quarantineTitle(diff?.attribution)}
                </div>
                <p className="text-amber-800">{quarantineMessage(diff?.attribution)}</p>
                <p className="mt-3 text-slate-500 text-[12px]">
                  Use the two documents on the right to compare this version pair directly.
                </p>
              </div>
            ) : (
              <>
                {/* count summary — mirrors the US "N sections total · X added · …" */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-3 py-2 border-b border-slate-100">
                  <span className="font-semibold text-slate-700">{sections.length} sections total</span>
                  {VERDICT_ORDER.filter((v) => v !== 'unchanged').map((v) => counts[v] > 0 && (
                    <span key={v} className={VERDICT_META[v].count}>{counts[v]} {VERDICT_META[v].label.toLowerCase()}</span>
                  ))}
                  {figTotals.added > 0 && (
                    <span className="text-emerald-600">{figTotals.added} figure{figTotals.added > 1 ? 's' : ''} added</span>
                  )}
                  {figTotals.removed > 0 && (
                    <span className="text-rose-500">{figTotals.removed} figure{figTotals.removed > 1 ? 's' : ''} removed</span>
                  )}
                  <span className="text-slate-400">{counts.unchanged} unchanged</span>
                </div>
                {/* verdict filters — plain names, same vocabulary as the summary */}
                <div className="flex flex-wrap gap-1 px-3 py-2 border-b border-slate-100">
                  {FILTERS.map(([f, lbl]) => {
                    // hide the Figures chip entirely when nothing changed there.
                    if (f === 'figures' && figTotals.sections === 0) return null;
                    const isFig = f === 'figures';
                    return (
                      <button key={f} onClick={() => setFilter(f)}
                              className={`text-[11px] px-2.5 py-0.5 rounded-full transition-colors ${filter === f
                                ? (isFig ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-white')
                                : (isFig ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                         : 'bg-slate-50 text-slate-500 hover:bg-slate-100')}`}>
                        {lbl}{isFig ? ` · ${figTotals.sections} section${figTotals.sections > 1 ? 's' : ''}` : ''}
                      </button>
                    );
                  })}
                </div>
                <div className="flex-1 overflow-y-auto">
                  {tree.has ? (
                    // HIERARCHICAL rail (Parser-B tree = nav authority): part →
                    // presentation group → section → subsection, mirroring the
                    // main EU label viewer. Section leaves join their diff row by
                    // anchor_id for verdict badges.
                    <>
                      {PART_ORDER.map((part) => {
                        const roots = tree.byPart.get(part);
                        if (!roots || !roots.length) return null;
                        const anyVisible = !visibleAnchors
                          || roots.some((r) => visibleAnchors.has(r.anchor_id));
                        if (!anyVisible) return null;
                        return (
                          <div key={part}>
                            <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-50 border-y border-slate-100 sticky top-0 z-10">
                              {PART_LABELS[part] || part}
                            </div>
                            {roots.map((r) => renderNode(r, 0))}
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    // legacy flat payload (no structure tree) — unchanged behavior
                    visible.map((s) => (
                      <SectionRow key={sectionKey(s)} s={s}
                                  isSel={selected === sectionKey(s)} onSelect={select} />
                    ))
                  )}
                  {tree.has && visibleAnchors && visibleAnchors.size === 0 && (
                    <div className="px-3 py-6 text-center text-gray-400 text-xs">Nothing matches this filter.</div>
                  )}
                  {!tree.has && visible.length === 0 && (
                    <div className="px-3 py-6 text-center text-gray-400 text-xs">Nothing matches this filter.</div>
                  )}
                </div>
                {changed.length > 0 && (
                  <>
                    <div className="flex items-center gap-2 px-3 py-2.5 border-t border-slate-100 bg-slate-50/70 shrink-0">
                      <button onClick={() => step(-1)} className="flex-1 px-2 py-1.5 rounded-md border border-slate-200 bg-white text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors">← Previous</button>
                      <button onClick={() => step(1)} className="flex-1 px-2 py-1.5 rounded-md bg-slate-800 text-white text-xs font-medium hover:bg-slate-900 transition-colors">Next change →</button>
                    </div>
                    <div className="px-3 pb-2 text-[10px] text-slate-300 text-center shrink-0">← / → or j / k to navigate</div>
                  </>
                )}
              </>
            )}
          </div>

          <div className="relative flex flex-1 min-w-0">
            <Pane side="old" pages={diff.old.pages} hls={diff.highlights.old} cited={citedOld} show={showChanges} paneRef={oldPane}
                  viewing={sel ? `§${sel.num} ${sel.title}` : ''} page={sel?.old_page ?? null} tag={oldTag}
                  overlay={source?.pageOverlay ? (pg) => source.pageOverlay!('old', oldV, pg) : undefined} />
          </div>
          <div className="w-px bg-slate-200" />
          <div className="relative flex flex-1 min-w-0">
            <Pane side="new" pages={diff.new.pages} hls={diff.highlights.new} cited={citedNew} show={showChanges} paneRef={newPane}
                  viewing={sel ? `§${sel.num} ${sel.title}` : ''} page={sel?.new_page ?? null} tag={newTag}
                  overlay={source?.pageOverlay ? (pg) => source.pageOverlay!('new', newV, pg) : undefined} />
          </div>
        </div>
      )}
    </div>
  );
}
